/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// DriveTelemetry Phase 1 — PURE rating core (no Firebase, no native, no React).
//
// The native DriveTelemetryPlugin emits a per-trip summary; this module turns
// one summary into a 0–100 score + a 5-tier label, and turns the last few trip
// scores into the DISPLAYED rating (a weighted average so one noisy trip can't
// crater a good driver). Everything here is a pure function so it can be unit-
// tested in Vitest without a device — the simulator cannot generate real
// motion, so the math must be independently verifiable.
//
// ⚠️ SCOPE = 6 factors ONLY (Phase 1): hard braking, hard acceleration, sharp
// cornering, smoothness, distance, time-of-day. No speeding/speed-limit, no
// distraction, no weather, no route regularity, no driver-vs-passenger.
//
// ⚠️ Every weight/threshold below is a CALIBRATION TARGET — tuned on real
// drives, kept in `SCORING` so there are no magic numbers in the logic.

/** The raw per-trip summary emitted by the native plugin (`tripSummary`). */
export interface TripSummary {
  tripId: string;
  /** Unix seconds. */
  startTs: number;
  endTs: number;
  durationSec: number;
  distanceMeters: number;
  harshBrakes: TelemetryEvent[];
  harshAccels: TelemetryEvent[];
  sharpCorners: TelemetryEvent[];
  /** Raw GPS speed samples (m/s); the native layer also sends smoothnessScore. */
  speedSamples?: number[];
  /** 0 (erratic) … 1 (steady), computed natively as 1 - rmsLongitudinal/ref
   *  (RMS of 50 Hz longitudinal acceleration). Replaced the old speed-CV metric,
   *  which conflated stop-and-go traffic with jerky driving. */
  smoothnessScore: number;
  /** m/s. */
  maxSpeed: number;
  isLateNight: boolean;
}

export interface TelemetryEvent {
  /** Unix seconds. */
  ts: number;
  /** Event magnitude in m/s² (longitudinal for brake/accel, lateral for corner). */
  intensity: number;
}

/** Five-tier rating label (worst → best). */
export type DriveRatingTier =
  | "needs_improvement"
  | "poor"
  | "good"
  | "very_good"
  | "excellent";

export interface TripScore {
  tripId: string;
  /** 0–100, higher is safer. */
  score: number;
  endTs: number;
  /** Per-factor penalty breakdown (for debugging / future surfacing). */
  breakdown: {
    braking: number;
    acceleration: number;
    cornering: number;
    smoothness: number;
    lateNight: number;
  };
}

export interface DriveRating {
  tier: DriveRatingTier;
  /** Weighted average score across the retained trips, 0–100. */
  score: number;
  /** How many trips contributed (1–3 in Phase 1). */
  tripCount: number;
  /** End timestamp (unix sec) of the most recent contributing trip. */
  lastTripEndTs: number;
}

// ── Calibration ──────────────────────────────────────────────────────────────
// CALIBRATION TARGETS. Penalties are in score-points subtracted from 100.
export const SCORING = {
  /** Trips shorter than this contribute event counts but their event RATES are
   *  unreliable; we floor the distance used for per-mile rates. */
  minScoredDistanceMeters: 800, // ~0.5 mi

  /** Points subtracted per harsh event, per mile, scaled by intensity over the
   *  native threshold. Cornering is weighted lighter (more ambiguous signal). */
  brakePenaltyPerEventPerMile: 6,
  accelPenaltyPerEventPerMile: 5,
  cornerPenaltyPerEventPerMile: 4,

  /** Intensity (m/s²) treated as "1×" severity; an event at 2× this contributes
   *  ~double its base penalty (capped). CALIBRATION TARGET — 4.5 from the
   *  2026-06-04 protocol drive: typical harsh events landed ~4–5 m/s² (→ ~1×)
   *  and the near-emergency stop ~6.1 (→ ~1.36×), a useful spread under the cap. */
  intensityReferenceMs2: 4.5,
  maxIntensityMultiplier: 2.0,

  /** Smoothness (0..1) maps to up to this many penalty points (1 = no penalty,
   *  0 = full penalty). */
  smoothnessMaxPenalty: 20,

  /** Flat penalty if the trip is late-night (context risk, not driver fault —
   *  kept small so it nudges rather than dominates). */
  lateNightPenalty: 4,

  /** Per-factor penalty ceilings so a single bad factor can't zero the score. */
  maxBrakePenalty: 35,
  maxAccelPenalty: 30,
  maxCornerPenalty: 25,

  /** Last-N trips averaged for the displayed rating; weights are most-recent
   *  first and need not sum to 1 (normalized internally). */
  retainedTrips: 3,
  recencyWeights: [0.5, 0.3, 0.2],

  /** Score → tier cutoffs (inclusive lower bound). */
  tierCutoffs: {
    excellent: 90,
    very_good: 75,
    good: 60,
    poor: 40,
    // below `poor` → needs_improvement
  },
} as const;

const METERS_PER_MILE = 1609.34;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Sum intensity-scaled severity for a set of events (each event ≥ 1×). */
function severitySum(events: TelemetryEvent[]): number {
  return events.reduce((acc, e) => {
    const mult = clamp(
      e.intensity / SCORING.intensityReferenceMs2,
      1,
      SCORING.maxIntensityMultiplier,
    );
    return acc + mult;
  }, 0);
}

/**
 * Score a single trip 0–100 (higher = safer). Pure; no I/O.
 *
 * Event penalties are per-mile so a long calm highway drive isn't punished for
 * having more total events than a short one. Distance is floored at
 * `minScoredDistanceMeters` so a 200 m trip with one hard brake doesn't read as
 * "one brake every 200 m → catastrophic".
 */
export function scoreTrip(trip: TripSummary): TripScore {
  const miles =
    Math.max(trip.distanceMeters, SCORING.minScoredDistanceMeters) /
    METERS_PER_MILE;

  const braking = Math.min(
    SCORING.maxBrakePenalty,
    (severitySum(trip.harshBrakes) / miles) *
      SCORING.brakePenaltyPerEventPerMile,
  );
  const acceleration = Math.min(
    SCORING.maxAccelPenalty,
    (severitySum(trip.harshAccels) / miles) *
      SCORING.accelPenaltyPerEventPerMile,
  );
  const cornering = Math.min(
    SCORING.maxCornerPenalty,
    (severitySum(trip.sharpCorners) / miles) *
      SCORING.cornerPenaltyPerEventPerMile,
  );
  const smoothness =
    (1 - clamp(trip.smoothnessScore, 0, 1)) * SCORING.smoothnessMaxPenalty;
  const lateNight = trip.isLateNight ? SCORING.lateNightPenalty : 0;

  const score = clamp(
    100 - braking - acceleration - cornering - smoothness - lateNight,
    0,
    100,
  );

  return {
    tripId: trip.tripId,
    score: Math.round(score),
    endTs: trip.endTs,
    breakdown: {
      braking: Math.round(braking),
      acceleration: Math.round(acceleration),
      cornering: Math.round(cornering),
      smoothness: Math.round(smoothness),
      lateNight,
    },
  };
}

/** Map a 0–100 score to a tier. */
export function tierForScore(score: number): DriveRatingTier {
  const c = SCORING.tierCutoffs;
  if (score >= c.excellent) return "excellent";
  if (score >= c.very_good) return "very_good";
  if (score >= c.good) return "good";
  if (score >= c.poor) return "poor";
  return "needs_improvement";
}

/**
 * Compute the DISPLAYED rating from the retained trip scores. Most-recent trip
 * is weighted highest (recencyWeights[0]); older trips taper off. Accepts trips
 * in ANY order — sorts by endTs descending internally — and uses at most
 * `retainedTrips`. Returns null if there are no trips.
 */
export function computeDisplayRating(scores: TripScore[]): DriveRating | null {
  if (scores.length === 0) return null;

  const ordered = [...scores]
    .sort((a, b) => b.endTs - a.endTs)
    .slice(0, SCORING.retainedTrips);

  let weightedSum = 0;
  let weightTotal = 0;
  ordered.forEach((trip, i) => {
    const w = SCORING.recencyWeights[i] ?? 0;
    weightedSum += trip.score * w;
    weightTotal += w;
  });

  const score = weightTotal > 0 ? Math.round(weightedSum / weightTotal) : 0;

  return {
    tier: tierForScore(score),
    score,
    tripCount: ordered.length,
    lastTripEndTs: ordered[0].endTs,
  };
}

/**
 * Fold a freshly-scored trip into the retained list, keeping only the most
 * recent `retainedTrips` (by endTs). Pure — the caller persists the result.
 */
export function retainTrips(
  existing: TripScore[],
  incoming: TripScore,
): TripScore[] {
  return [...existing, incoming]
    .sort((a, b) => b.endTs - a.endTs)
    .slice(0, SCORING.retainedTrips);
}
